/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// === EA: Judge.me shop-rating fallback (MOB-BADGE01 v5) ===
// Naar et produkt har 0 anmeldelser, vises butiksratingen (4.9★ 288) som fallback.
// Strategi: Hardkodet butiksrating + polling hvert 500ms i 5 minutter.
// Ingen afhengighed af store_summary timing.
// Implementeret: 3. juli 2026
(function () {
  'use strict';

  // Hardkodet butiksrating - opdater naar ratingen aendrer sig markant
  var SHOP_RATING = 4.9;
  var SHOP_REVIEW_COUNT = 288;
  var SHOP_REVIEW_TEXT = SHOP_REVIEW_COUNT + ' anmeldelser';

  var applied = false;

  function applyShopRatingFallback() {
    if (applied) return true;

    var badge = document.querySelector('.jdgm-prev-badge');
    if (!badge) return false;

    var productReviews = parseInt(badge.getAttribute('data-number-of-reviews') || '0', 10);
    if (productReviews !== 0) {
      applied = true;
      return true; // Produktet har egne anmeldelser - ingen fallback noedvendig
    }

    // Opdater data-attributter
    badge.setAttribute('data-average-rating', SHOP_RATING.toFixed(2));
    badge.setAttribute('data-number-of-reviews', SHOP_REVIEW_COUNT);

    // Opdater stjerner
    var starsEl = badge.querySelector('.jdgm-prev-badge__stars');
    if (starsEl) {
      starsEl.setAttribute('data-score', SHOP_RATING.toFixed(2));
      var stars = '';
      var rounded = Math.round(SHOP_RATING * 2) / 2;
      for (var i = 1; i <= 5; i++) {
        if (i <= Math.floor(rounded)) {
          stars += '<span class="jdgm-star jdgm--on"></span>';
        } else if (i - 0.5 === rounded) {
          stars += '<span class="jdgm-star jdgm--half"></span>';
        } else {
          stars += '<span class="jdgm-star jdgm--off"></span>';
        }
      }
      starsEl.innerHTML = stars;
    }

    // Opdater tekst
    var textEl = badge.querySelector('.jdgm-prev-badge__text');
    if (textEl) {
      textEl.textContent = SHOP_REVIEW_TEXT;
    }

    // Tving synlighed
    badge.style.setProperty('display', 'block', 'important');

    applied = true;
    return true;
  }

  // Poll hvert 500ms i op til 5 minutter
  var attempts = 0;
  var maxAttempts = 600; // 5 minutter
  var interval = setInterval(function () {
    attempts++;
    if (applyShopRatingFallback() || attempts >= maxAttempts) {
      clearInterval(interval);
    }
  }, 500);

  // Ogsaa lyt paa Judge.me events hvis tilgaengelige
  document.addEventListener('jdgm:all:load', function () {
    applyShopRatingFallback();
  });
  document.addEventListener('jdgm:badge:load', function () {
    applyShopRatingFallback();
  });

})();

// ============================================================
// SEO FIX: Judge.me carousel "Read more" link mangler href
// Lighthouse: "Links kan ikke crawles" (jdgm-carousel__rev-sum-read-more)
// Fix: Tilføj href="#" til alle Read more links så de er crawlable
// Implementeret: 4. juli 2026
// ============================================================
(function() {
  function fixJdgmReadMoreLinks() {
    var links = document.querySelectorAll('a.jdgm-carousel__rev-sum-read-more:not([href])');
    links.forEach(function(link) {
      link.setAttribute('href', '#');
    });
  }

  // Kør ved carousel load (Judge.me's eget event)
  document.addEventListener('jdgm:carousel:load', fixJdgmReadMoreLinks);
  // Kør også ved all:load som fallback
  document.addEventListener('jdgm:all:load', fixJdgmReadMoreLinks);
  // Kør ved DOMContentLoaded som ekstra fallback
  document.addEventListener('DOMContentLoaded', function() {
    setTimeout(fixJdgmReadMoreLinks, 500);
  });
})();

// =================================================================================================
// EA: Filter drawer — auto-expand og sort-sektion håndtering
// Flyttet fra theme.liquid til custom.js for bedre vedligeholdelse
// =================================================================================================

// Auto-expand alle filter-detaljer når mobil filter-drawer åbnes
document.addEventListener('DOMContentLoaded', function() {
  function expandAllFilters() {
    var facetFilters = document.querySelector('facet-filters');
    if (!facetFilters) return;
    var details = facetFilters.querySelectorAll('details.filter.disclosure');
    details.forEach(function(d) { d.open = true; });
  }
  var observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(m) {
      if (m.attributeName === 'aria-hidden') {
        var el = m.target;
        if (el.getAttribute('aria-hidden') === 'false') {
          setTimeout(expandAllFilters, 50);
        }
      }
    });
  });
  var facetFilters = document.querySelector('facet-filters');
  if (facetFilters) {
    observer.observe(facetFilters, { attributes: true });
  }
  expandAllFilters();
});

// Luk "Filtre efter" (sort) sektion som standard, hold alle andre åbne
(function() {
  function initFilterSort() {
    var filterSort = document.getElementById('filter-sort');
    if (filterSort) filterSort.removeAttribute('open');
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilterSort);
  } else {
    initFilterSort();
  }
  document.addEventListener('on:drawer:open', function(e) {
    if (e.detail && e.detail.name === 'facet-filters') {
      setTimeout(function() {
        var filterSort = document.getElementById('filter-sort');
        if (filterSort) filterSort.removeAttribute('open');
      }, 10);
    }
  });
  var observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(m) {
      m.addedNodes.forEach(function(node) {
        if (node.nodeType === 1) {
          var fs = node.id === 'filter-sort' ? node : node.querySelector && node.querySelector('#filter-sort');
          if (fs) fs.removeAttribute('open');
        }
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();

/* EA-STATUS-BAR-MERGE-START */
(function() {
  function mergeStatusBar() {
    var invText = document.querySelector('product-inventory .product-inventory__text');
    var delivBadge = document.querySelector('.product-info__sticky > .ea-delivery-badge');
    if (!invText || !delivBadge) return;
    // Undgå at flytte den mere end én gang
    if (invText.querySelector('.ea-delivery-badge--inline')) return;
    // Klon delivery badge og tilføj inline klasse
    var clone = delivBadge.cloneNode(true);
    clone.classList.add('ea-delivery-badge--inline');
    clone.classList.remove('ea-delivery-badge');
    invText.appendChild(clone);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mergeStatusBar);
  } else {
    mergeStatusBar();
  }

  // Kør også ved variant-skift (Shopify custom events)
  document.addEventListener('variant:change', function() {
    setTimeout(mergeStatusBar, 100);
  });
})();
/* EA-STATUS-BAR-MERGE-END */

/* ============================================================
   HEADER — SKJUL SØGEFELT VED SCROLL (2026-07-07)
   ============================================================ */
(function() {
  var SCROLL_THRESHOLD = 60; // px fra toppen før søgefelt skjules
  var storeHeader = document.querySelector('store-header');
  if (!storeHeader) return;

  var lastScrollY = window.scrollY;
  var ticking = false;

  function updateSearchVisibility() {
    var scrollY = window.scrollY;
    if (scrollY > SCROLL_THRESHOLD) {
      storeHeader.classList.add('ea-search-hidden');
    } else {
      storeHeader.classList.remove('ea-search-hidden');
    }
    lastScrollY = scrollY;
    ticking = false;
  }

  window.addEventListener('scroll', function() {
    if (!ticking) {
      requestAnimationFrame(updateSearchVisibility);
      ticking = true;
    }
  }, { passive: true });
})();
/* ============================================================
   END HEADER SØGEFELT SCROLL
   ============================================================ */

/* ============================================================
   STICKY ATC PANEL — SMART LOGIK (2026-07-07 v2)
   Logik:
   1. Single-variant (1 variant): Vis ATC-knap når hoved-ATC er ude af viewport
   2. Multi-variant (>1 variant): Skjul sticky panel via CSS (variant-vælger er synlig)
   3. Panel starter SKJULT — vises kun når hoved-ATC-knap scroller ud af viewport
   ============================================================ */
(function() {
  function addToCartDirect(variantId, btn) {
    if (!variantId) return;
    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Tilføjer...';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      // Tjek om svaret er en fejl (f.eks. out of stock)
      if (data.status && data.status !== 200) {
        throw new Error(data.description || 'Fejl');
      }
      // Trigger tema's cart update event
      document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      // Åbn cart drawer hvis tilgængelig
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        // Fallback: opdater cart count i header
        var cartCount = document.querySelector('.cart-count, [data-cart-count], .js-cart-count');
        if (cartCount) {
          fetch('/cart.js')
            .then(function(r) { return r.json(); })
            .then(function(cart) {
              cartCount.textContent = cart.item_count;
            });
        }
      }
      btn.textContent = 'Tilføjet ✓';
      btn.style.backgroundColor = '#16a34a';
      btn.style.borderColor = '#16a34a';
      setTimeout(function() {
        btn.textContent = originalText;
        btn.style.backgroundColor = '';
        btn.style.borderColor = '';
        btn.disabled = false;
      }, 2000);
    })
    .catch(function() {
      btn.textContent = originalText;
      btn.disabled = false;
    });
  }

  function isInViewport(el) {
    var rect = el.getBoundingClientRect();
    var windowHeight = window.innerHeight || document.documentElement.clientHeight;
    // Returnerer true hvis elementet er synligt i viewport (med margin for sticky header)
    return rect.top < (windowHeight - 60) && rect.bottom > 60;
  }

  function initStickyAtcPanel() {
    var panel = document.querySelector('sticky-atc-panel');
    if (!panel) return;

    var variantsCount = parseInt(panel.getAttribute('data-variants-count') || '0', 10);
    var variantId = panel.getAttribute('data-variant-id');

    // SCENARIO 2: Multi-variant produkter — CSS skjuler panelet
    if (variantsCount > 1) {
      return;
    }

    // SCENARIO 1: Single-variant produkt — tilføj click-handler
    var stickyAtcBtn = panel.querySelector('.ea-sticky-atc-btn');
    if (stickyAtcBtn && variantId) {
      stickyAtcBtn.addEventListener('click', function() {
        addToCartDirect(variantId, stickyAtcBtn);
      });
    }

    // Find hoved-ATC-knap på siden
    var mainAtcBtn = document.querySelector('button[name="add"]');
    if (!mainAtcBtn) {
      mainAtcBtn = document.querySelector('.product-form__submit, form[action="/cart/add"] button[type="submit"]');
    }

    if (!mainAtcBtn) {
      // Ingen hoved-ATC-knap fundet — vis altid sticky panel
      panel.classList.remove('sticky-atc-panel--out', 'invisible');
      panel.classList.add('ea-sticky-visible');
      return;
    }

    // KRITISK: Tjek synlighed SYNKRONT ved initialisering
    // Panel starter skjult og vises kun når hoved-ATC er ude af viewport
    function updatePanelVisibility(isAtcVisible) {
      if (isAtcVisible) {
        // Hoved-ATC er synlig — skjul sticky panel
        panel.classList.remove('ea-sticky-visible');
        panel.classList.add('ea-sticky-hidden');
        panel.classList.add('sticky-atc-panel--out');
      } else {
        // Hoved-ATC er ude af viewport — vis sticky panel
        panel.classList.remove('ea-sticky-hidden', 'sticky-atc-panel--out', 'invisible');
        panel.classList.add('ea-sticky-visible');
      }
    }

    // Tjek initial synlighed
    updatePanelVisibility(isInViewport(mainAtcBtn));

    // IntersectionObserver for løbende opdatering
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        updatePanelVisibility(entry.isIntersecting);
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px 0px 0px'
    });
    observer.observe(mainAtcBtn);
  }

  // Kør så hurtigt som muligt efter DOM er klar
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyAtcPanel);
  } else if (document.readyState === 'interactive') {
    initStickyAtcPanel();
  } else {
    // DOM er allerede fuldt indlæst
    initStickyAtcPanel();
  }
})();
/* ============================================================
   END STICKY ATC PANEL SMART LOGIK
   ============================================================ */
